import type { CityConfig } from "../city-template/types";

export const hendersonNVConfig: CityConfig = {
  city: "Henderson",
  state: "Nevada",
  stateAbbr: "NV",
  slug: "henderson-nv",
  route: "/cities/henderson-nv",
  title: "Emergency Passport Henderson NV | Expedited Passport Renewal | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Henderson, NV — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Henderson and Clark County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Henderson, NV",
  heroSubtitle: "Passport expired before your trip? Need emergency passport renewal in Henderson? We help Clark County travelers get expedited passports in as little as 24 hours. No agency visit required — Nevada's fastest passport courier service.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Henderson%20Nevada%20city%20skyline%20at%20sunset%20with%20Lake%20Mead%20in%20the%20distance%2C%20Sloan%20Canyon%20rocky%20desert%20terrain%2C%20modern%20suburban%20master-planned%20community%20homes%20and%20commercial%20areas%2C%20warm%20amber%20sunset%20light%20over%20the%20Nevada%20desert%20landscape%2C%20wide%20panoramic%20view%20of%20the%20greater%20Las%20Vegas%20metro%20southern%20suburbs%2C%20clean%20and%20modern%20cityscape%20with%20desert%20mountains%20backdrop%2C%20no%20text%20or%20people%20visible%2C%20professional%20photography&width=1800&height=700&seq=henderson-nv-hero-001&orientation=landscape",
  heroImageAlt: "Henderson Nevada city skyline at sunset with Lake Mead and Sloan Canyon desert landscape",
  geoPosition: "36.0397;-114.9817",
  geoPlacename: "Henderson, Nevada",
  lastModified: "2026-06-02",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "300 N Los Angeles St, Los Angeles, CA 90012",
    distance: "Located in downtown Los Angeles — approximately 285 miles from Henderson via I-15",
  },
  nearbyCitiesZips: [
    { name: "Henderson", zips: "89002, 89014, 89015" },
    { name: "Green Valley", zips: "89014, 89074" },
    { name: "Anthem", zips: "89044, 89052" },
    { name: "Cadence", zips: "89002" },
    { name: "Lake Las Vegas", zips: "89011" },
    { name: "Boulder City", zips: "89005, 89006" },
    { name: "Whitney", zips: "89014, 89121" },
    { name: "Sloan", zips: "89054" },
    { name: "Enterprise", zips: "89141, 89148" },
    { name: "Paradise Hills", zips: "89103" },
    { name: "North Las Vegas", zips: "89030, 89031" },
    { name: "Sunrise Manor", zips: "89110" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Henderson departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — ship from any UPS in Henderson" },
  ],
  internalLinks: [
    { name: "Emergency passport services in Las Vegas", route: "/cities/las-vegas" },
    { name: "Fast passport renewal in Reno", route: "/cities/reno" },
    { name: "Expedited passport help in Sparks", route: "/cities/sparks" },
    { name: "Urgent passport services in Los Angeles", route: "/cities/los-angeles" },
    { name: "Same-day passport help near Boulder City", route: "/cities/las-vegas" },
    { name: "Passport courier services in Arizona", route: "/states/arizona" },
  ],
  fpcOfficeAddress: "300 N Los Angeles St, Los Angeles, CA 90012",
  fpcOfficeNote: "Fast Passport Center serves Henderson remotely via secure courier. We do not have a walk-in office in Henderson — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Henderson and Clark County travelers.",
  fpcOfficeDistance: "Serving all of Henderson and Southern Clark County",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Henderson and surrounding Clark County. From Green Valley to Anthem, Lake Las Vegas to Boulder City — we help Henderson travelers get their passports fast without the 285-mile drive to Los Angeles.",
  nearbyCitiesIntro: "We serve all Henderson neighborhoods and nearby communities including Green Valley, Anthem, Lake Las Vegas, Cadence, Boulder City, Whitney, and the southern Clark County communities. If you are in Henderson or southern Las Vegas metro, we have you covered.",
  howItWorksIntro: "Most Henderson travelers complete everything online and ship documents overnight. No need to drive to Los Angeles or wait at a regional passport agency. Our emergency passport process is designed for Southern Nevada travelers who need fast, reliable results.",
  mistakesIntro: "Henderson residents often underestimate how far the nearest passport agency is. Here are the most common passport mistakes we see.",
  testimonialsIntro: "Henderson travelers trust us when their international departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Henderson? Here are the most common ones we hear from Clark County travelers.",
  statePageRoute: "/states/nevada",
  statePageName: "Nevada",
  dmvWebsite: "https://dmv.nv.gov",
  dmvLabel: "Nevada DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Nearest agency is Los Angeles (285 miles)",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Henderson travelers with immediate departure needs — as fast as 24 hours for urgent international flights from Harry Reid International.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support across Henderson and Clark County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Henderson NV travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens in Henderson NV with a passport issued within the last 15 years." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the Clark County acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Michelle R.",
      location: "Green Valley, NV",
      rating: 5,
      text: "Our family cruise was in 4 days and I discovered my husband's passport had expired. Fast Passport Center got him a renewal in 36 hours. We made it to Cabo!",
      service: "Emergency Passport",
    },
    {
      name: "Ethan C.",
      location: "Anthem, NV",
      rating: 5,
      text: "Business travel to Dubai required a passport valid 6 months beyond my trip. Found out a week before. They renewed mine in 3 days. Fantastic service.",
      service: "Passport Renewal",
    },
    {
      name: "Lisa P.",
      location: "Henderson, NV",
      rating: 5,
      text: "First passport for my teenage daughter. They walked us through the whole process, helped us find the nearest acceptance facility, and we had her passport in 5 days.",
      service: "New Passport",
    },
    {
      name: "Gary N.",
      location: "Lake Las Vegas, NV",
      rating: 5,
      text: "Lost my passport before an international golf trip to Scotland. They replaced it in 2 days. I made the tournament and shot a 72 at St Andrews. Worth every penny.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "henv-1",
      name: "Henderson Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "120 Water St",
      city: "Henderson",
      state: "NV",
      zip: "89015",
      phone: "(702) 564-9901",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from Henderson downtown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Henderson facility. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "henv-2",
      name: "Green Valley Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2460 Windmill Pkwy",
      city: "Henderson",
      state: "NV",
      zip: "89074",
      phone: "(702) 617-8255",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "In Green Valley Ranch area",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Green Valley and Cadence residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "henv-3",
      name: "Anthem Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2650 W Horizon Ridge Pkwy",
      city: "Henderson",
      state: "NV",
      zip: "89052",
      phone: "(702) 614-5099",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "In the Anthem community area",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Anthem, MacDonald Ranch, and southern Henderson. Call ahead for appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "henv-4",
      name: "Henderson City Hall",
      type: "City Government Passport Acceptance",
      address: "240 S Water St",
      city: "Henderson",
      state: "NV",
      zip: "89015",
      phone: "(702) 267-2000",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "Downtown Henderson",
      website: "https://www.cityofhenderson.com/",
      websiteLabel: "Henderson City Hall",
      notes: "City administrative offices with extended hours. Full-service passport acceptance. Appointments recommended.",
      icon: "ri-government-line",
    },
    {
      id: "henv-5",
      name: "Boulder City Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1230 Arizona St",
      city: "Boulder City",
      state: "NV",
      zip: "89005",
      phone: "(702) 293-3531",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "25 miles from Henderson downtown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Alternative for Boulder City and Lake Mead area residents. Limited hours — call ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "henv-6",
      name: "Nevada DMV — Henderson",
      type: "State DMV Acceptance Facility",
      address: "2250 N Green Valley Pkwy",
      city: "Henderson",
      state: "NV",
      zip: "89014",
      phone: "(702) 486-4368",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Green Valley area, East Henderson",
      website: "https://dmv.nv.gov/",
      websiteLabel: "Nevada DMV",
      notes: "Full-service Henderson DMV. REAL ID and driver's license renewals. Select services available with appointment.",
      icon: "ri-car-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 285 miles to Los Angeles", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Henderson photo shops often use generic settings. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Henderson acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Henderson applicants show up with incorrect amounts and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Los Angeles regional agency will turn you away. Don't make the 285-mile drive without documentation.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Henderson families at the acceptance clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Nevada has no local passport agency. Standard processing takes 8–11 weeks and the nearest agency is in Los Angeles. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Henderson." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Henderson address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Henderson, NV?",
      a: "Fast Passport Center offers emergency passport processing in Henderson in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities. Since Nevada has no local passport agency — the nearest is in Los Angeles, 285 miles away — our courier service is the fastest reliable option for Henderson residents.",
    },
    {
      q: "Does Nevada have a passport agency near Henderson?",
      a: "No. Nevada has no regional passport agency. Henderson travelers must either drive 285 miles to the Los Angeles Passport Agency for emergency appointments, or use Fast Passport Center's 24–48 hour courier service. The courier option is almost always faster, more convenient, and eliminates the long-distance drive.",
    },
    {
      q: "Where can I get a fast passport renewal in Henderson?",
      a: "Fast Passport Center offers fast passport renewal in Henderson with no office visit required for most adult renewals. We serve Green Valley, Anthem, Lake Las Vegas, Cadence, and all Henderson neighborhoods. Complete your application online, ship your documents using our prepaid overnight label, and our couriers handle everything else.",
    },
    {
      q: "What passport acceptance facilities are in Henderson?",
      a: "Henderson has multiple acceptance facilities including the Henderson Main Post Office (89015), Green Valley Post Office (89074), Anthem Post Office (89052), and Henderson City Hall. All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I fly internationally from Las Vegas with a Henderson passport?",
      a: "Absolutely. Harry Reid International Airport (LAS) is approximately 15 miles from Henderson and serves as the primary international gateway for all Clark County residents. Fast Passport Center can process your Henderson passport renewal or emergency passport in 24–48 hours so you make your international flight on time.",
    },
    {
      q: "Can I expedite a child passport in Henderson?",
      a: "Yes. Fast Passport Center expedites child passports for Henderson families. Both parents must appear at the acceptance facility or the absent parent must submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Henderson families through the entire process and help schedule acceptance clerk appointments.",
    },
    {
      q: "What if I need a passport fast for a cruise leaving from Los Angeles?",
      a: "Los Angeles is a common port for cruises, and it's 285 miles from Henderson. Fast Passport Center can get your passport processed in 24–48 hours — far faster than making the drive to the Los Angeles Passport Agency and waiting for an emergency appointment. Start your application online today.",
    },
    {
      q: "How much does expedited passport service cost in Henderson?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour, 3–5 day expedited, or standard options. Contact us for a quote based on your departure date.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Green Valley", address: "2680 Sunset Rd, Henderson, NV 89014", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Anthem", address: "2650 W Horizon Ridge Pkwy, Henderson, NV 89052", hours: "Mon–Sun: 7AM–11PM" },
    { name: "UPS Store — Henderson", address: "101 N Stephanie St, Henderson, NV 89014", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Henderson", address: "1300 W Warm Springs Rd, Henderson, NV 89014", hours: "Mon–Sun: 6AM–11PM" },
    { name: "FedEx Office — Henderson", address: "120 Market St, Henderson, NV 89015", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "Walgreens — Henderson Downtown", address: "1800 E Sahara Ave, Henderson, NV 89104", hours: "Mon–Sun: 7AM–11PM" },
    { name: "CVS Pharmacy — Lake Mead", address: "1000 N Boulder Hwy, Henderson, NV 89002", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Sam's Club — Henderson", address: "1910 W Craig Rd, Henderson, NV 89052", hours: "Mon–Fri: 10AM–8PM, Sat–Sun: 9AM–8PM" },
  ],
  dmvLocations: [
    {
      name: "Nevada DMV — Henderson",
      address: "2250 N Green Valley Pkwy, Henderson, NV 89014",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service Henderson DMV. REAL ID processing. Online appointment required.",
      featured: true,
    },
    {
      name: "Nevada DMV — Boulder City",
      address: "700 Wyoming St, Boulder City, NV 89005",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Small DMV for Boulder City and Lake Mead area residents. Limited hours.",
      featured: false,
    },
    {
      name: "Nevada DMV — West Flamingo",
      address: "8250 W Flamingo Rd, Las Vegas, NV 89147",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Alternative full-service DMV for Henderson travelers going toward the Las Vegas Strip area.",
      featured: false,
    },
    {
      name: "Henderson City Hall",
      address: "240 S Water St, Henderson, NV 89015",
      hours: "Mon–Fri: 8AM–5PM",
      note: "City government services including passport-related document acceptance.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "LAS",
      name: "Harry Reid International Airport",
      distance: "15 miles from Henderson downtown",
      address: "5757 Wayne Newton Blvd, Las Vegas, NV 89119",
      note: "The primary international airport for Henderson travelers. Over 50 million passengers annually. Major hub for international flights to Mexico, Canada, and Europe.",
      flightUrl: "https://www.harryreidairport.com/",
      flightLabel: "Harry Reid Airport",
      featured: true,
    },
    {
      code: "BOU",
      name: "Boulder City Municipal Airport",
      distance: "25 miles from Henderson",
      address: "1 Airport Rd, Boulder City, NV 89005",
      note: "Small general aviation airport with Grand Canyon tour flights. Not for international travel.",
      flightUrl: "https://www.bcity.com/airport",
      flightLabel: "Boulder City Airport",
      featured: false,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "285 miles from Henderson via I-15",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Extended international routes. Home to the nearest Los Angeles Regional Passport Agency for emergency appointments — but the drive makes our courier service far more practical.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
  ],
};